import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logActivity } from "../../utils/activity";
import { loadGmHudConfig } from "../../utils/config";
import { usePendingReports } from "../../context/PendingReports";
import "./Topbar.css";

// The pages a GM moves between, in the order they sit in the bar. `perm` names the per-user tick
// in the staff sheet that opens the page; no perm means everyone on shift. Hiding is cosmetic —
// games-cloud enforces the same ticks on every call.
const NAV = [
    { path: "/", label: "Forside", activity: "open_home" },
    { path: "/checkliste", label: "Todo", activity: "open_checklist" },
    { path: "/fejlmeldinger", label: "Fejlmelding", activity: "open_fault_reports" },
    { path: "/walk-in", label: "Walk-in", perm: "booking", activity: "open_walkin" },
    { path: "/ekstra-spillere", label: "Ekstra spillere", activity: "open_extra_players" },
    { path: "/scoreboard", label: "Scoreboard", perm: "scoreboard", activity: "open_scoreboard" },
    { path: "/admin", label: "Admin", perm: "admin", activity: "open_admin" },
];

const Topbar = ({ logo, userEmail, onLogout, onEndGame, children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { count } = usePendingReports();
    const [userCfg, setUserCfg] = useState(null);

    // The bar carries the whole menu on every page, so a GM moves straight from the walk-in page to
    // the todo list without going through the front page. It needs the user's permissions to know
    // what to show; loadGmHudConfig is cached, so this is one request per session.
    useEffect(() => {
        const email = (localStorage.getItem("userEmail") || "").toLowerCase();
        if (!email) return;
        loadGmHudConfig().then(cfg => setUserCfg(cfg?.users?.[email] || null));
    }, []);

    // Every item stays on the bar on every page, the current one marked rather than removed: a menu
    // that drops the page you are on is a menu whose buttons move under the cursor. "Afslut Spil" is
    // available to every logged-in user — the previous per-user `endGame` gate has been removed.

    // On a room's HUD the button opens the report inline (onEndGame); elsewhere it routes to the page.
    const handleEndGame = () => {
        logActivity("open_end_game");
        if (onEndGame) onEndGame();
        else navigate("/afslut-spil");
    };

    const loggedIn = !!localStorage.getItem("accessToken");
    // Fejlmelding is open to every logged-in user (its staged-rollout tick was dropped); the
    // gated tabs stay hidden until the sheet says otherwise.
    const nav = loggedIn ? NAV.filter(item => !item.perm || !!userCfg?.[item.perm]) : [];
    // Who is signed in, and the way out, belong on every page for the same reason the menu does.
    const email = userEmail ?? localStorage.getItem("userEmail") ?? "";

    // Pages that hold their own login state (Home) hand in an onLogout; everywhere else the bar
    // signs out on its own and reloads the front page, which is the shortest path to a clean slate.
    const handleLogout = onLogout ?? (() => {
        logActivity("logout");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userEmail");
        window.location.assign("/");
    });

    return (
        <div className="topbar">
            {/* The logo is the way home from anywhere — the oldest convention on the web, and the
                reason there is no separate back button on any page. */}
            <button
                type="button"
                className="topbar-logo"
                title="Til forsiden"
                onClick={() => { logActivity("open_home"); navigate("/"); }}
            >
                <img className="topbar-mark" src="/riddlehouse-logo.svg" alt="" />
                {logo ?? <>Riddle<span>house</span> GM</>}
            </button>
            {/* The stretch lives here, not on the logo: a logo with flex:1 makes the whole empty
                middle of the bar a link home, which is a click nobody meant to make. */}
            <div className="topbar-spacer" />
            {email && <span className="topbar-user">{email}</span>}
            <nav className="topbar-nav">
                {nav.map(item => (
                    <React.Fragment key={item.path}>
                        <button
                            className={`topbar-btn accent ${item.path === location.pathname || (item.path !== "/" && location.pathname.startsWith(item.path + "/")) ? "active" : ""}`}
                            aria-current={item.path === location.pathname ? "page" : undefined}
                            onClick={() => { logActivity(item.activity); navigate(item.path); }}
                        >
                            {item.label}
                        </button>
                        {/* "Afslut Spil" sits after Forside, where the shift's most-used button belongs. */}
                        {item.path === "/" && (
                            <button
                                className={`topbar-btn accent topbar-endgame ${location.pathname === "/afslut-spil" ? "active" : ""}`}
                                onClick={handleEndGame}
                            >
                                Afslut Spil
                                {count > 0 && <span className="topbar-badge">{count > 99 ? "99+" : count}</span>}
                            </button>
                        )}
                    </React.Fragment>
                ))}
            </nav>
            {children}
            {loggedIn && (
                <button className="topbar-btn danger" onClick={handleLogout}>Log ud</button>
            )}
        </div>
    );
};

export default Topbar;
